import { z } from 'zod';
import { sheetFilterSchema } from '@yana/shared';

/** A received layout is display state; it never grants access to extra columns. */
export const sheetObservationViewSchema = z.object({
  activeViewId: z.string().nullable(),
  search: z.string().max(100),
  sort: z.array(z.object({ field: z.string(), dir: z.enum(['asc', 'desc']) })).max(3),
  filters: z.array(sheetFilterSchema).max(20),
  columnFilters: z.record(z.array(z.string())),
  columns: z.array(z.string()).max(200),
  widths: z.record(z.number().finite().min(40).max(1200)),
  pins: z.record(z.enum(['left']).nullable()),
});

export const sheetObservationGridSchema = z.object({
  density: z.enum(['compact', 'normal', 'comfortable', 'lines2', 'lines4']),
  groupBy: z.string().nullable(),
  groupMode: z.enum(['value', 'alphabet', 'phone']),
  collapsed: z.array(z.string()),
  collapsedGroups: z.array(z.string()),
  expanded: z.array(z.string()),
});

export type SheetObservationView = z.infer<typeof sheetObservationViewSchema>;

export function allowedSheetObservationView(
  input: unknown,
  availableFields: readonly string[],
): SheetObservationView | null {
  const result = sheetObservationViewSchema.safeParse(input);
  if (!result.success) return null;
  const allowed = new Set(availableFields);
  const view = result.data;
  const fieldsOnly = <T>(values: Record<string, T>): Record<string, T> =>
    Object.fromEntries(Object.entries(values).filter(([key]) => allowed.has(key)));
  return {
    ...view,
    sort: view.sort.filter((item) => allowed.has(item.field)),
    filters: view.filters.filter((item) => allowed.has(item.field)),
    columns: [...new Set(view.columns)].filter((key) => allowed.has(key)),
    columnFilters: fieldsOnly(view.columnFilters),
    widths: fieldsOnly(view.widths),
    pins: fieldsOnly(view.pins),
  };
}
